// Hiring gap analysis engine
// Analyzes the behavioral composition of a group of people and identifies
// what trait profiles are thin, balanced, or heavy — then generates target
// recruiting profiles to rebalance.

const TRAIT_INFO = {
  A: { name: "Autonomy", low: "supporters", high: "drivers" },
  B: { name: "Social", low: "analytical/independent", high: "communicators" },
  C: { name: "Pace", low: "fast/urgent", high: "steady/methodical" },
  D: { name: "Conformity", low: "conceptual/flexible", high: "detail/quality-driven" },
};

// Bucket a trait value: low (0-4), mid (5), high (6-10)
function bucket(v) {
  if (v <= 4) return "low";
  if (v >= 6) return "high";
  return "mid";
}

// Analyze distribution of one trait across a group
export function analyzeTrait(people, trait) {
  const counts = { low: 0, mid: 0, high: 0 };
  people.forEach(p => {
    const v = p[`Survey_${trait}`];
    if (v !== null && v !== undefined) counts[bucket(v)]++;
  });
  const total = counts.low + counts.mid + counts.high || 1;
  const pct = {
    low: Math.round((counts.low / total) * 100),
    mid: Math.round((counts.mid / total) * 100),
    high: Math.round((counts.high / total) * 100),
  };

  // Verdict logic: a healthy org wants representation across the spectrum.
  // Flag "thin" on the end that's notably underrepresented (<20%),
  // "heavy" if one end dominates (>45%).
  let verdict, verdictType, insight;
  const info = TRAIT_INFO[trait];

  if (pct.high < 20) {
    verdict = `Thin on High-${trait}`;
    verdictType = "thin";
    insight = `Only ${pct.high}% are high-${trait} (${info.high}). The workforce skews ${info.low}.`;
  } else if (pct.low < 20) {
    verdict = `Thin on Low-${trait}`;
    verdictType = "thin";
    insight = `Only ${pct.low}% are low-${trait} (${info.low}). The workforce skews ${info.high}.`;
  } else if (pct.high > 45) {
    verdict = `Heavy on High-${trait}`;
    verdictType = "heavy";
    insight = `${pct.high}% are high-${trait} (${info.high}). Well-covered — don't over-index here in new hires.`;
  } else if (pct.low > 45) {
    verdict = `Heavy on Low-${trait}`;
    verdictType = "heavy";
    insight = `${pct.low}% are low-${trait} (${info.low}). Well-covered on this end.`;
  } else {
    verdict = "Balanced";
    verdictType = "ok";
    insight = "Healthy spread across the spectrum. No action needed.";
  }

  return { trait, name: info.name, counts, pct, verdict, verdictType, insight };
}

// Analyze all four primary traits
export function analyzeComposition(people) {
  return ["A", "B", "C", "D"].map(t => analyzeTrait(people, t));
}

// Build target recruiting profiles from the gaps.
// Returns prioritized list of target profiles with trait ranges.
export function buildTargetProfiles(composition) {
  const targets = [];

  // Find the thin traits — those are what we recruit for
  const thinHigh = composition.filter(c => c.verdict.startsWith("Thin on High"));
  const thinLow = composition.filter(c => c.verdict.startsWith("Thin on Low"));

  // Primary target: address the biggest high-side gaps (most common in construction:
  // thin on C and D = need steady, detail-driven field anchors)
  const needC = thinHigh.find(c => c.trait === "C");
  const needD = thinHigh.find(c => c.trait === "D");

  if (needC || needD) {
    targets.push({
      archetype: "Craftsman",
      icon: "tools",
      title: "The Steady Anchor",
      idealFor: "Superintendent / Field Leadership",
      priority: 1,
      ranges: { A: [3, 6], B: [2, 5], C: [6, 9], D: [6, 9] },
      altArchetypes: [
        { name: "Specialist", icon: "microscope" },
        { name: "Guardian", icon: "shield" },
      ],
      why: "Field crews are running fast and light on detail. A high-C, high-D superintendent brings the methodical, quality-focused stability that's currently scarce — the person who keeps a long job on the rails. Lower autonomy is fine; you have enough drivers. Look for someone who finishes what they start and sets their watch by the schedule.",
    });
  }

  const needA = thinHigh.find(c => c.trait === "A");
  if (needA) {
    targets.push({
      archetype: "Trailblazer",
      icon: "flag",
      title: "The Driver",
      idealFor: "Project Manager / Lead",
      priority: targets.length + 1,
      ranges: { A: [7, 10], B: [4, 7], C: [3, 6], D: [4, 7] },
      altArchetypes: [
        { name: "Enterpriser", icon: "rocket" },
        { name: "Daredevil", icon: "bolt" },
      ],
      why: "The org is short on high-autonomy leaders who own outcomes and drive pace. Recruit assertive, visionary PMs who can take charge of a project and push it forward without heavy oversight.",
    });
  }

  const needB = thinHigh.find(c => c.trait === "B");
  if (needB) {
    targets.push({
      archetype: "Persuader",
      icon: "microphone",
      title: "The Connector",
      idealFor: "Client-facing PM / BD",
      priority: targets.length + 1,
      ranges: { A: [5, 8], B: [7, 10], C: [3, 6], D: [3, 6] },
      altArchetypes: [
        { name: "Rainmaker", icon: "cloud-rain" },
        { name: "Influencer", icon: "speakerphone" },
      ],
      why: "Communication and relationship-building are thin. Recruit high-social people who energize teams, manage client relationships, and keep cross-trade communication flowing.",
    });
  }

  // If everything is balanced, say so
  if (targets.length === 0) {
    targets.push({
      archetype: null,
      icon: "circle-check",
      title: "Well-Balanced Workforce",
      idealFor: "Hire for role need, not behavioral gap",
      priority: 1,
      ranges: null,
      altArchetypes: [],
      why: "Your current behavioral composition is well-balanced across all four traits. For upcoming hires, prioritize role/skill needs and project fit rather than correcting a behavioral imbalance.",
    });
  }

  return targets;
}

// Per-team gap: what's missing on a specific crew
export function analyzeTeamGap(teamPeople) {
  const comp = analyzeComposition(teamPeople);
  const gaps = [];

  // For a team, check the key construction requirements
  const hasDriver = teamPeople.some(p => p.Survey_A >= 6);
  const hasAnchor = teamPeople.some(p => p.Survey_C >= 6);
  const hasQuality = teamPeople.some(p => p.Survey_D >= 6);
  const hasGlue = teamPeople.some(p => p.Survey_B >= 6);

  if (!hasDriver) gaps.push({ trait: "A", need: "high", label: "No clear driver", note: "No one with high autonomy to own outcomes and drive pace." });
  if (!hasAnchor) gaps.push({ trait: "C", need: "high", label: "No steady anchor", note: "No methodical, patient member to stabilize the work." });
  if (!hasQuality) gaps.push({ trait: "D", need: "high", label: "Quality at risk", note: "No detail-driven member for safety, compliance, and quality control." });
  if (!hasGlue) gaps.push({ trait: "B", need: "high", label: "Communication gap", note: "No strong communicator to hold the team together socially." });

  return { composition: comp, gaps, hasDriver, hasAnchor, hasQuality, hasGlue };
}

export function rangeToText(ranges) {
  if (!ranges) return "";
  return Object.entries(ranges).map(([t, [lo, hi]]) => `${t} ${lo}-${hi}`).join(" · ");
}
